import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { API_BASE_URL } from '../config/api';
import SideBar from './DashBoard/SideBar';
import Navbar from './DashBoard/Navbar';
import RichTextEditor from './RichTextEditor';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBook,
  faChalkboardUser,
  faTag,
  faAlignLeft,
  faVideo,
  faImage,
  faArrowLeft,
  faCheckCircle,
  faSpinner,
  faExclamationCircle,
  faLayerGroup
} from "@fortawesome/free-solid-svg-icons";

function EditCourse() {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const location = useLocation();
  const courseId = location.pathname.split("/")[2];

  const [formData, setFormData] = useState({
    course_name: '',
    instructor: '',
    price: '',
    description: '',
    y_link: '',
    p_link: '',
    category_id: '',
  });

  useEffect(() => {
    if (!localStorage.getItem("adminId")) {
      navigate("/admin-login");
    }

    // Fetch course data
    async function fetchCourse() {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/courses/${courseId}`);
        const fetchedCourse = response.data;
        setFormData({
          course_name: fetchedCourse.course_name || fetchedCourse.courseName || '',
          instructor: fetchedCourse.instructor || fetchedCourse.tutor || '',
          price: fetchedCourse.price || '',
          description: fetchedCourse.description || '',
          y_link: fetchedCourse.y_link || fetchedCourse.video || '',
          p_link: fetchedCourse.p_link || fetchedCourse.photo || '',
          category_id: fetchedCourse.category_id || '',
        });
      } catch (err) {
        setError('Error fetching course');
      }
    }
    fetchCourse();

    // Fetch categories
    fetch(`${API_BASE_URL}/api/categories`)
      .then((res) => res.json())
      .then((data) => setCategories(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [courseId, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.category_id) {
      setError('Please select a category for this course.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/courses/${courseId}`,
        formData
      );

      if (response.status === 200) {
        navigate("/DCourses");
        toast.success('Updated successfully', {
          position: 'top-right',
          autoClose: 1000,
        });
      } else {
        setError('Course update failed');
      }
    } catch (err) {
      setError('Error updating course: ' + (err.message || ''));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}>
      <SideBar current={"courses"} />
      <section id="content">
        <Navbar />
        <main style={{ padding: "28px", maxWidth: "900px", margin: "0 auto" }}>

          {/* Header */}
          <div style={{ marginBottom: "28px" }}>
            <button
              onClick={() => navigate("/DCourses")}
              style={{
                background: "transparent",
                border: "none",
                color: "#4f46e5",
                fontWeight: 700,
                fontSize: "0.9rem",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginBottom: "8px",
                padding: 0
              }}
            >
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Courses
            </button>
            <h1 style={{ color: "#0f172a", fontFamily: "'Outfit', sans-serif", fontSize: "1.8rem", fontWeight: 800, margin: 0 }}>
              Edit Course
            </h1>
            <p style={{ color: "#64748b", margin: "4px 0 0", fontSize: "0.9rem" }}>
              Update course details, category, and media links
            </p>
          </div>

          {/* Form Card */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: "20px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.05)",
              padding: "36px",
              animation: "fadeIn 0.4s ease-out"
            }}
          >
            {error && (
              <div
                style={{
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  color: "#ef4444",
                  padding: "14px 18px",
                  borderRadius: "12px",
                  fontSize: "0.9rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginBottom: "24px"
                }}
              >
                <FontAwesomeIcon icon={faExclamationCircle} />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              {/* Row 1: Course Name & Instructor */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "20px" }}>
                <div>
                  <label style={{ display: "block", color: "#334155", fontSize: "0.88rem", fontWeight: 700, marginBottom: "8px" }}>
                    Course Title *
                  </label>
                  <div style={{ position: "relative" }}>
                    <FontAwesomeIcon icon={faBook} style={{ position: "absolute", left: "16px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
                    <input
                      type="text"
                      name="course_name"
                      placeholder="Course title"
                      value={formData.course_name}
                      onChange={handleChange}
                      required
                      style={{
                        width: "100%",
                        padding: "12px 16px 12px 44px",
                        border: "1.5px solid #cbd5e1",
                        borderRadius: "12px",
                        fontSize: "0.95rem",
                        color: "#0f172a",
                        outline: "none",
                        boxSizing: "border-box"
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: "block", color: "#334155", fontSize: "0.88rem", fontWeight: 700, marginBottom: "8px" }}>
                    Instructor *
                  </label>
                  <div style={{ position: "relative" }}>
                    <FontAwesomeIcon icon={faChalkboardUser} style={{ position: "absolute", left: "16px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
                    <input
                      type="text"
                      name="instructor"
                      placeholder="Instructor name"
                      value={formData.instructor}
                      onChange={handleChange}
                      required
                      style={{
                        width: "100%",
                        padding: "12px 16px 12px 44px",
                        border: "1.5px solid #cbd5e1",
                        borderRadius: "12px",
                        fontSize: "0.95rem",
                        color: "#0f172a",
                        outline: "none",
                        boxSizing: "border-box"
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Row 2: Price & Category */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "20px" }}>
                <div>
                  <label style={{ display: "block", color: "#334155", fontSize: "0.88rem", fontWeight: 700, marginBottom: "8px" }}>
                    Price ($ USD) *
                  </label>
                  <div style={{ position: "relative" }}>
                    <FontAwesomeIcon icon={faTag} style={{ position: "absolute", left: "16px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
                    <input
                      type="number"
                      name="price"
                      placeholder="e.g. 49"
                      value={formData.price}
                      onChange={handleChange}
                      required
                      style={{
                        width: "100%",
                        padding: "12px 16px 12px 44px",
                        border: "1.5px solid #cbd5e1",
                        borderRadius: "12px",
                        fontSize: "0.95rem",
                        color: "#0f172a",
                        outline: "none",
                        boxSizing: "border-box"
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: "block", color: "#334155", fontSize: "0.88rem", fontWeight: 700, marginBottom: "8px" }}>
                    Category *
                    <span style={{ color: "#ef4444", fontSize: "0.78rem", marginLeft: "6px", fontWeight: 600 }}>(Required)</span>
                  </label>
                  <div style={{ position: "relative" }}>
                    <FontAwesomeIcon icon={faLayerGroup} style={{ position: "absolute", left: "16px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", zIndex: 1 }} />
                    <select
                      name="category_id"
                      value={formData.category_id}
                      onChange={handleChange}
                      required
                      style={{
                        width: "100%",
                        padding: "12px 16px 12px 44px",
                        border: "1.5px solid #cbd5e1",
                        borderRadius: "12px",
                        fontSize: "0.95rem",
                        color: formData.category_id ? "#0f172a" : "#94a3b8",
                        outline: "none",
                        boxSizing: "border-box",
                        background: "#fff",
                        appearance: "auto",
                        cursor: "pointer"
                      }}
                    >
                      <option value="">— Select a Category —</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Row 3: Video Link */}
              <div>
                <label style={{ display: "block", color: "#334155", fontSize: "0.88rem", fontWeight: 700, marginBottom: "8px" }}>
                  Video / YouTube URL *
                </label>
                <div style={{ position: "relative" }}>
                  <FontAwesomeIcon icon={faVideo} style={{ position: "absolute", left: "16px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
                  <input
                    type="text"
                    name="y_link"
                    placeholder="https://www.youtube.com/watch?v=..."
                    value={formData.y_link}
                    onChange={handleChange}
                    required
                    style={{
                      width: "100%",
                      padding: "12px 16px 12px 44px",
                      border: "1.5px solid #cbd5e1",
                      borderRadius: "12px",
                      fontSize: "0.95rem",
                      color: "#0f172a",
                      outline: "none",
                      boxSizing: "border-box"
                    }}
                  />
                </div>
              </div>

              {/* Row 4: Image Link */}
              <div>
                <label style={{ display: "block", color: "#334155", fontSize: "0.88rem", fontWeight: 700, marginBottom: "8px" }}>
                  Course Cover Image URL *
                </label>
                <div style={{ position: "relative" }}>
                  <FontAwesomeIcon icon={faImage} style={{ position: "absolute", left: "16px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
                  <input
                    type="text"
                    name="p_link"
                    placeholder="https://images.unsplash.com/photo-..."
                    value={formData.p_link}
                    onChange={handleChange}
                    required
                    style={{
                      width: "100%",
                      padding: "12px 16px 12px 44px",
                      border: "1.5px solid #cbd5e1",
                      borderRadius: "12px",
                      fontSize: "0.95rem",
                      color: "#0f172a",
                      outline: "none",
                      boxSizing: "border-box"
                    }}
                  />
                </div>
              </div>

              {/* Row 5: Description — Rich Text Editor */}
              <div>
                <label style={{ display: "block", color: "#334155", fontSize: "0.88rem", fontWeight: 700, marginBottom: "8px" }}>
                  <FontAwesomeIcon icon={faAlignLeft} style={{ marginRight: "8px", color: "#94a3b8" }} />
                  Course Description *
                </label>
                <RichTextEditor
                  value={formData.description}
                  onChange={(html) => setFormData(prev => ({ ...prev, description: html }))}
                  placeholder="Course description..."
                />
                <p style={{ margin: "6px 0 0", fontSize: "0.78rem", color: "#94a3b8" }}>
                  Use the toolbar to add bold, italic, bullets, and alignment formatting.
                </p>
              </div>

              {/* Actions */}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "12px" }}>
                <button
                  type="button"
                  onClick={() => navigate("/DCourses")}
                  style={{
                    background: "#f1f5f9",
                    color: "#475569",
                    border: "none",
                    padding: "12px 24px",
                    borderRadius: "10px",
                    fontWeight: 700,
                    cursor: "pointer"
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    background: "linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)",
                    color: "#ffffff",
                    border: "none",
                    padding: "12px 28px",
                    borderRadius: "10px",
                    fontWeight: 700,
                    cursor: loading ? "wait" : "pointer",
                    boxShadow: "0 4px 14px rgba(79, 70, 229, 0.35)",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px"
                  }}
                >
                  {loading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin /> Saving...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheckCircle} /> Update Course
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </main>
      </section>
    </div>
  );
}

export default EditCourse;